import ExcelJS from 'exceljs';
import { format } from 'date-fns';

interface ActivityLog {
  user_name?: string;
  action_type: string;
  site_name?: string | null;
  created_at: string;
  action_details: any;
}

interface SummaryReportData {
  startDate: Date;
  endDate: Date;
  overdueItems: Array<{ staffName: string; trainingTitle: string; dueDate: string; daysOverdue: number }>;
  unbeddedClients: Array<{ first_name: string; last_name: string; site_name?: string | null; updated_at?: string }>;
  serviceBreakdown: Record<string, number>;
  facilityAlerts: Array<{ title: string; description?: string | null; category?: string | null; site_name?: string | null; created_at: string }>;
  clientAlerts: Array<{ title: string; description?: string | null; category?: string | null; site_name?: string | null; created_at: string }>;
  logs: ActivityLog[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const SKIP_KEYS = new Set([
  'id', 'client_id', 'user_id', 'site_id', 'room_id', 'alert_id',
  'note_id', 'roster_id', 'inspection_id', 'document_id', 'staff_id',
  'mention_id', 'parent_note_id', 'partner_id', 'case_worker_id',
  'created_at', 'updated_at', 'timestamp', 'time', 'checked_in_at',
  'checked_out_at', 'acknowledged_at', 'reported_at', 'resolved_at',
]);

const formatDetails = (raw: any): string => {
  if (!raw) return '';
  if (typeof raw !== 'object') return String(raw);
  const parts: string[] = [];
  for (const [key, value] of Object.entries(raw)) {
    if (value == null || value === '') continue;
    if (SKIP_KEYS.has(key)) continue;
    const str = String(value);
    if (UUID_RE.test(str)) continue;
    if (ISO_DATE_RE.test(str)) continue;
    parts.push(str);
  }
  return parts.join(', ');
};

const styleHeader = (row: ExcelJS.Row) => {
  row.font = { bold: true };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
};

const addSectionTitle = (sheet: ExcelJS.Worksheet, title: string) => {
  const row = sheet.addRow([title]);
  row.font = { bold: true, size: 13 };
  sheet.addRow([]);
};

export const exportSummaryReport = async (data: SummaryReportData) => {
  const workbook = new ExcelJS.Workbook();

  // === Overview sheet ===
  const overview = workbook.addWorksheet('Summary');
  overview.columns = [{ width: 32 }, { width: 20 }];

  overview.addRow(['Summary Report']).font = { bold: true, size: 16 };
  overview.addRow([
    `${format(data.startDate, 'MMM d, yyyy')} – ${format(data.endDate, 'MMM d, yyyy')}`,
  ]).font = { italic: true, color: { argb: 'FF666666' } };
  overview.addRow([]);

  const totalServices = Object.values(data.serviceBreakdown).reduce((s, v) => s + v, 0);
  const metricRows: Array<[string, number]> = [
    ['Overdue Trainings', data.overdueItems.length],
    ['Unbedded Clients', data.unbeddedClients.length],
    ['Total Services Logged', totalServices],
    ['Active Facility Alerts', data.facilityAlerts.length],
    ['Active Client Alerts', data.clientAlerts.length],
    ['Total Activity Actions', data.logs.length],
  ];
  styleHeader(overview.addRow(['Metric', 'Count']));
  metricRows.forEach(r => overview.addRow(r));

  // === Overdue Trainings ===
  const trainings = workbook.addWorksheet('Overdue Trainings');
  trainings.columns = [
    { header: 'Staff', key: 'staff', width: 25 },
    { header: 'Training', key: 'training', width: 35 },
    { header: 'Due Date', key: 'due', width: 18 },
    { header: 'Days Overdue', key: 'days', width: 15 },
  ];
  styleHeader(trainings.getRow(1));
  data.overdueItems.forEach(i =>
    trainings.addRow({
      staff: i.staffName,
      training: i.trainingTitle,
      due: format(new Date(i.dueDate), 'MMM dd, yyyy'),
      days: i.daysOverdue,
    }),
  );

  // === Unbedded Clients ===
  const unbedded = workbook.addWorksheet('Unbedded Clients');
  unbedded.columns = [
    { header: 'Client', key: 'name', width: 28 },
    { header: 'Facility', key: 'facility', width: 22 },
    { header: 'Last Updated', key: 'updated', width: 22 },
  ];
  styleHeader(unbedded.getRow(1));
  data.unbeddedClients.forEach(c =>
    unbedded.addRow({
      name: `${c.first_name} ${c.last_name}`,
      facility: c.site_name || '—',
      updated: c.updated_at ? format(new Date(c.updated_at), 'MMM dd, yyyy HH:mm') : '—',
    }),
  );

  // === Services ===
  const services = workbook.addWorksheet('Services');
  services.columns = [
    { header: 'Service Type', key: 'type', width: 28 },
    { header: 'Count', key: 'count', width: 12 },
  ];
  styleHeader(services.getRow(1));
  Object.entries(data.serviceBreakdown)
    .sort((a, b) => b[1] - a[1])
    .forEach(([type, count]) => services.addRow({ type, count }));
  const totalRow = services.addRow({ type: 'Total', count: totalServices });
  totalRow.font = { bold: true };

  // === Alerts ===
  const alerts = workbook.addWorksheet('Active Alerts');
  alerts.columns = [
    { header: 'Scope', key: 'scope', width: 12 },
    { header: 'Title', key: 'title', width: 30 },
    { header: 'Category', key: 'category', width: 18 },
    { header: 'Facility', key: 'facility', width: 22 },
    { header: 'Created', key: 'created', width: 22 },
    { header: 'Description', key: 'description', width: 40 },
  ];
  styleHeader(alerts.getRow(1));
  [
    ...data.facilityAlerts.map(a => ({ ...a, scope: 'Facility' })),
    ...data.clientAlerts.map(a => ({ ...a, scope: 'Client' })),
  ].forEach(a =>
    alerts.addRow({
      scope: a.scope,
      title: a.title,
      category: a.category || '—',
      facility: a.site_name || '—',
      created: format(new Date(a.created_at), 'MMM dd, yyyy HH:mm'),
      description: a.description || '',
    }),
  );

  // === Activity Log (full detail) ===
  const logSheet = workbook.addWorksheet('Activity Log');
  logSheet.columns = [
    { header: 'User', key: 'user', width: 25 },
    { header: 'Action', key: 'action', width: 22 },
    { header: 'Facility', key: 'facility', width: 22 },
    { header: 'Date & Time', key: 'datetime', width: 22 },
    { header: 'Details', key: 'details', width: 40 },
  ];
  styleHeader(logSheet.getRow(1));
  data.logs.forEach(log =>
    logSheet.addRow({
      user: log.user_name || 'Unknown',
      action: log.action_type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      facility: log.site_name || '—',
      datetime: format(new Date(log.created_at), 'MMM dd, yyyy HH:mm'),
      details: formatDetails(log.action_details),
    }),
  );

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `summary-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

// Backwards compatibility — kept for any callers still importing the old name.
export const exportActivityLogs = async (logs: ActivityLog[]) => {
  await exportSummaryReport({
    startDate: new Date(),
    endDate: new Date(),
    overdueItems: [],
    unbeddedClients: [],
    serviceBreakdown: {},
    facilityAlerts: [],
    clientAlerts: [],
    logs,
  });
};
